import {ptdeId} from '@algoquest/contracts';
import {preferences} from '../settings/preferences';
import {actionSound,playSound} from "../audio/gameAudio";
import * as THREE from "three";
import type { AlgorithmOperation } from "@algoquest/schemas";
import { createPrototypeScene, type PrototypeSceneHandles } from "../scenes/PrototypeScene";
import { CameraRig } from "../camera/CameraRig";
import { GridInputController } from "../input/GridInputController";
import { createBoxRow, type BoxRowHandles } from "../entities/Box";
import { createSortBoxRow, type SortBoxRowHandles } from "../entities/SortBoxRow";
import { createPlayerRobot, type PlayerRobotHandles } from "../entities/PlayerRobot";
import { createMidMarker, type MidMarkerHandles } from "../entities/MidMarker";
import { createComparePointer, type ComparePointerHandles } from "../entities/ComparePointer";
import { createSearchController } from "../algorithms/searching/createSearchController";
import type { SearchActionOutcome, SearchController, SearchViewState } from "../algorithms/searching/SearchController";
import { createSortController } from "../algorithms/sorting/createSortController";
import type { SortActionOutcome, SortController, SortDecision, SortViewState } from "../algorithms/sorting/SortController";
import type { LoadedLevel } from "../levels/levelLoader";

const MID_MARKER_BASE_Y = 1.3;
const COMPARE_POINTER_BASE_Y = 1.3;

export interface GameRuntimeCallbacks {
  onNearestBoxChange?: (index: number | null) => void;
  // Searching (Linear Search / Binary Search via SearchController, Phase 04).
  onViewReset?: (view: SearchViewState) => void;
  onViewStep?: (view: SearchViewState, operations: AlgorithmOperation[]) => void;
  onActionRejected?: (expectedIndex: number) => void;
  onChoiceRejected?: () => void;
  // Sorting (Bubble Sort / Selection Sort / Insertion Sort via
  // SortController, Phase 08). Through Phase 07 this was three separate
  // callback vocabularies (onSortLevelReset/onSortStep,
  // onSelectionLevelReset/onSelectionStep, onInsertionLevelReset/
  // onInsertionStep) — one per algorithm, mirroring the Phase 02/03
  // per-algorithm naming precedent Searching had before Phase 04. Now
  // that all three sorting algorithms speak the same SortViewState shape,
  // a single onSortViewReset/onSortViewStep pair replaces all three,
  // exactly mirroring onViewReset/onViewStep above.
  onSortViewReset?: (view: SortViewState) => void;
  onSortViewStep?: (view: SortViewState, operations: AlgorithmOperation[]) => void;
  onSortActionRejected?: (expectedIndex: number) => void;
  onSortDecisionRejected?: () => void;
}

/**
 * Owns the full Three.js presentation lifecycle: init -> loadLevel -> start
 * -> pause/resume -> reset -> dispose (Master Spec §5, §47). Everything
 * about *which* algorithm is running is owned by a per-stage abstraction
 * this class talks to — `SearchController` for Searching (§13, unified in
 * Phase 04) and, as of Phase 08, `SortController` for Sorting (§13,
 * "Sorting vertical slice"), unifying Bubble Sort, Selection Sort, and
 * Insertion Sort behind one interaction contract exactly the way Phase 04
 * unified Linear Search and Binary Search behind `SearchController`. This
 * class never knows which concrete algorithm is behind either controller
 * — it only renders whatever `SearchViewState`/`SortViewState` comes back
 * and dispatches input through `handlePrimaryAction`/`handleDirectionChoice`
 * (Searching) or `handlePrimaryAction`/`handleDecision` (Sorting).
 *
 * Phase 05 note: this class branches on a `stage: "searching" | "sorting"`
 * flag, each with its own entities (BoxRow/MidMarker vs. SortBoxRow/
 * ComparePointer) and its own interaction methods. Unlike the temporary
 * per-algorithm branches Phase 03 (Searching) and Phase 05/06/07 (Sorting)
 * each carried before their respective unification phases, Searching and
 * Sorting are genuinely different game domains — hidden-value scanning
 * vs. visible-value reordering (§21 vs §24) — so this stage split is the
 * intended permanent shape, not a shortcut awaiting a future unification
 * pass.
 */
export class GameRuntime {
  private readonly canvas: HTMLCanvasElement;
  private readonly callbacks: GameRuntimeCallbacks;

  private renderer: THREE.WebGLRenderer | null = null;
  private sceneHandles: PrototypeSceneHandles | null = null;
  private cameraRig: CameraRig | null = null;
  private input: GridInputController | null = null;
  private player: PlayerRobotHandles | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private stage: "searching" | "sorting" = "searching";

  // Searching (Phase 04).
  private boxRow: BoxRowHandles | null = null;
  private midMarker: MidMarkerHandles | null = null;
  private controller: SearchController | null = null;

  // Sorting (Phase 05/06/07/08). sortBoxRow/comparePointer are shared
  // entities across every sorting algorithm (same presentation contract,
  // §24); `sortController` is whichever concrete SortController the
  // loaded level's algorithm maps to (Phase 08 — see createSortController).
  private sortBoxRow: SortBoxRowHandles | null = null;
  private comparePointer: ComparePointerHandles | null = null;
  private sortController: SortController | null = null;

  private rafHandle: number | null = null;
  private isRunning = false;
  private unsubscribePreferences:(()=>void)|null=null;
  private applyPreferences=()=>{if(!this.renderer)return;const low=preferences.get().graphics==='performance';this.renderer.setPixelRatio(low?1:Math.min(window.devicePixelRatio||1,2));this.renderer.shadowMap.enabled=!low;this.renderer.shadowMap.needsUpdate=true;this.sceneHandles?.scene.traverse(object=>{if(object instanceof THREE.Mesh){const materials=Array.isArray(object.material)?object.material:[object.material];materials.forEach(m=>m.needsUpdate=true);}});this.handleResize();};
  private isPaused = false;
  private lastTimestamp = 0;
  private lastNearestBoxIndex: number | null = null;
  private currentLevel: LoadedLevel | null = null;
  private markerElapsed = 0;

  private guidedComparisons=0;
  private manualSearch: {index:number|null;low:number;high:number;checked:number[];found:boolean}|null=null;
  setManualSearch(state:{index:number|null;low:number;high:number;checked:number[];found:boolean}){
    this.manualSearch=state;
    if(this.midMarker)this.midMarker.setVisible(false);
    const view=this.controller?.getView();
    view?.values.forEach((value,i)=>this.boxRow?.setBoxState(i,i<state.low||i>state.high?'eliminated':state.found&&i===state.index?'found':state.checked.includes(i)?'checked':'hidden',value));
  }
  private manualSort:{values:number[];pair:number|null;complete:boolean}|null=null;
  setManualSort(state:{values:number[];pair:number|null;complete:boolean}){
    this.manualSort=state;this.sortBoxRow?.setValues(state.values);
    state.values.forEach((_,i)=>this.sortBoxRow?.setState(i,state.complete?'sorted':state.pair!==null&&(i===state.pair||i===state.pair+1)?'comparing':'default'));
    this.comparePointer?.setVisible(state.pair!==null&&!state.complete);
    if(state.pair!==null&&this.comparePointer&&this.sortBoxRow)this.comparePointer.mesh.position.set((this.sortBoxRow.getBoxWorldX(state.pair)+this.sortBoxRow.getBoxWorldX(state.pair+1))/2,COMPARE_POINTER_BASE_Y,0);
  }
  private bugDemo=false;
  setBugDemo(active:boolean){this.bugDemo=active;}
  constructor(canvas: HTMLCanvasElement, callbacks: GameRuntimeCallbacks = {}, private readonly guidedSearch=false, private readonly revealSearchValues=false) {
    this.canvas = canvas;
    this.callbacks = callbacks;
  }

  init(): void {
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.sceneHandles = createPrototypeScene(this.guidedSearch?ptdeId.scene:undefined);
    this.cameraRig = new CameraRig(this.canvas);
    this.input = new GridInputController();
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.canvas.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("algoquest-move", this.onMove);

    this.applyPreferences();
    this.unsubscribePreferences=preferences.subscribe(this.applyPreferences);
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.canvas);
  }

  loadLevel(level: LoadedLevel): void {
    if (!this.sceneHandles) {
      throw new Error("GameRuntime.loadLevel called before init()");
    }

    const { definition } = level;
    this.currentLevel = level;
    this.clearLevelEntities();
    this.input?.clear();
    this.lastNearestBoxIndex = null;
    this.markerElapsed = 0;
    this.cameraRig?.setFrameWidth((definition.layout.boxCount-1)*definition.layout.spacing+3.5);
    this.cameraRig?.reset();

    if (definition.dataset.kind === "sorting") {
      if (
        definition.algorithm !== "bubble-sort" &&
        definition.algorithm !== "selection-sort" &&
        definition.algorithm !== "insertion-sort"
      ) {
        throw new Error(
          `Unsupported sorting algorithm "${definition.algorithm}" — this build only supports bubble-sort, selection-sort, and insertion-sort (Phase 08 scope).`,
        );
      }
      this.stage = "sorting";
      // Captured into locals right here, before any further method calls,
      // so the narrowed "sorting" dataset type is fixed at declaration
      // time rather than relied on across intervening calls.
      const { layout, dataset } = definition;
      const algorithm = definition.algorithm;

      const sortBoxRow = createSortBoxRow(dataset.values, layout.spacing);
      this.sortBoxRow = sortBoxRow;
      this.sceneHandles.scene.add(sortBoxRow.group);

      this.player = createPlayerRobot();
      this.player.setPositionX(sortBoxRow.getBoxWorldX(0));
      this.sceneHandles.scene.add(this.player.group);

      const comparePointer = createComparePointer();
      this.comparePointer = comparePointer;
      this.sceneHandles.scene.add(comparePointer.mesh);

      const sortController = createSortController(algorithm, dataset.values, dataset.sortDirection);
      this.sortController = sortController;
      const initialView = sortController.getView();
      this.applySortViewToBoxes(initialView);
      this.updateSortComparePointer(initialView);
      this.lastNearestBoxIndex = 0;
      sortBoxRow.setNearestIndex(0);
      this.callbacks.onNearestBoxChange?.(0);
      this.callbacks.onSortViewReset?.(initialView);
      return;
    }

    if (definition.dataset.kind !== "searching") {
      throw new Error("This build of GameRuntime only supports searching- and sorting-dataset levels (Phase 05 scope).");
    }
    if (definition.algorithm !== "linear-search" && definition.algorithm !== "binary-search") {
      throw new Error(
        `Unsupported algorithm "${definition.algorithm}" — this build only supports linear-search and binary-search (Phase 05 scope).`,
      );
    }
    this.stage = "searching";
    // Captured into locals right here, before any method call below, so the
    // narrowed "searching" dataset type is fixed at declaration time rather
    // than relied on across intervening calls.
    const { layout, dataset } = definition;
    const algorithm = definition.algorithm;

    this.boxRow = createBoxRow(layout.boxCount, layout.spacing);
    this.sceneHandles.scene.add(this.boxRow.group);

    this.player = createPlayerRobot();
    this.player.setPositionX(this.boxRow.getBoxWorldX(0));
    this.sceneHandles.scene.add(this.player.group);

    const midMarker = createMidMarker();
    this.midMarker = midMarker;
    this.sceneHandles.scene.add(midMarker.mesh);

    const controller = createSearchController(algorithm, dataset.values, dataset.target);
    this.controller = controller;
    const initialView = controller.getView();
    if(this.revealSearchValues || (this.guidedSearch && algorithm === "binary-search")) {
      dataset.values.forEach((value,index)=>this.boxRow!.setBoxState(index,"hidden",value));
    }
    this.updateMidMarker(initialView);
    this.lastNearestBoxIndex = 0;
    this.boxRow.setNearestIndex(0);
    this.callbacks.onNearestBoxChange?.(0);
    this.callbacks.onViewReset?.(initialView);
  }

  start(): void {
    this.isRunning = true;
    this.isPaused = false;
    this.lastTimestamp = 0;
    if (this.rafHandle === null) {
      this.rafHandle = requestAnimationFrame(this.tick);
    }
  }

  pause(): void {
    this.input?.clear();
    this.isPaused = true;
  }

  resume(): void {
    this.input?.clear();
    this.isPaused = false;
    this.lastTimestamp = 0;
  }

  reset(): void {
    if (this.currentLevel) {
      this.loadLevel(this.currentLevel);
    }
    this.isPaused = false;
  }

  resetCamera(): void {
    this.cameraRig?.reset();
  }

  dispose(): void {
    this.unsubscribePreferences?.();
    this.unsubscribePreferences=null;
    this.isRunning = false;
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("algoquest-move", this.onMove);
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.clearLevelEntities();
    this.sceneHandles?.dispose();
    this.cameraRig?.dispose();
    this.input?.dispose();
    this.renderer?.dispose();

    this.sceneHandles = null;
    this.cameraRig = null;
    this.input = null;
    this.renderer = null;
    this.currentLevel = null;
  }

  private clearLevelEntities(): void {
    const boxRow = this.boxRow;
    if (boxRow) {
      this.sceneHandles?.scene.remove(boxRow.group);
      boxRow.dispose();
      this.boxRow = null;
    }
    const sortBoxRow = this.sortBoxRow;
    if (sortBoxRow) {
      this.sceneHandles?.scene.remove(sortBoxRow.group);
      sortBoxRow.dispose();
      this.sortBoxRow = null;
    }
    const player = this.player;
    if (player) {
      this.sceneHandles?.scene.remove(player.group);
      player.dispose();
      this.player = null;
    }
    const midMarker = this.midMarker;
    if (midMarker) {
      this.sceneHandles?.scene.remove(midMarker.mesh);
      midMarker.dispose();
      this.midMarker = null;
    }
    const comparePointer = this.comparePointer;
    if (comparePointer) {
      this.sceneHandles?.scene.remove(comparePointer.mesh);
      comparePointer.dispose();
      this.comparePointer = null;
    }
    this.controller = null;
    this.sortController = null;
  }

  /** PTDE authorizes an exact number of comparisons; physical inputs cannot bypass it. */
  setGuidedComparisons(count:number):void {
    if(this.guidedSearch)this.guidedComparisons=Math.max(0,Math.floor(count));
  }

  // --- Searching interaction (algorithm-agnostic within Searching, Phase 04) --

  private tryAct(nearestIndex: number | null): void {
    const controller = this.controller;
    if (!controller) return;
    this.applySearchOutcome(controller.handlePrimaryAction(nearestIndex));
  }

  private tryChooseDirection(direction: "left" | "right"): void {
    const controller = this.controller;
    if (!controller) return;
    this.applySearchOutcome(controller.handleDirectionChoice(direction));
  }

  private applySearchOutcome(outcome: SearchActionOutcome): void {
    switch (outcome.kind) {
      case "accepted":
        playSound(actionSound(outcome.result.view.isComplete,outcome.result.operations));
        this.player?.react(outcome.result.view.isComplete?'success':'scan');
        this.applyViewToBoxes(outcome.result.view);
        this.updateMidMarker(outcome.result.view);
        this.callbacks.onViewStep?.(outcome.result.view, outcome.result.operations);
        break;
      case "rejected-wrong-box":
        this.player?.react('error');playSound('error');
        this.callbacks.onActionRejected?.(outcome.expectedIndex);
        break;
      case "rejected-wrong-direction":
        this.player?.react('error');playSound('error');
        this.callbacks.onChoiceRejected?.();
        break;
      case "ignored":
        break;
    }
  }

  private applyViewToBoxes(view: SearchViewState): void {
    const boxRow = this.boxRow;
    const level = this.currentLevel;
    if (!boxRow || !level) return;

    const dataset = level.definition.dataset;
    if (dataset.kind !== "searching") return;

    for (const index of view.checkedIndices) {
      const value = dataset.values[index];
      const visualState = view.foundIndex === index ? "found" : "checked";
      boxRow.setBoxState(index, visualState, value);
    }

    for (const [start, end] of view.eliminatedRanges) {
      for (let i = start; i <= end; i += 1) {
        boxRow.setBoxState(i, "eliminated");
      }
    }
  }

  private updateMidMarker(view: SearchViewState): void {
    const midMarker = this.midMarker;
    const boxRow = this.boxRow;
    if (!midMarker || !boxRow) return;
    if (view.focusIndex === null) {
      midMarker.setVisible(false);
      return;
    }
    const x = boxRow.getBoxWorldX(view.focusIndex);
    midMarker.mesh.position.set(x, MID_MARKER_BASE_Y, 0);
    midMarker.setVisible(true);
  }

  private updateMidMarkerBob(deltaSeconds: number): void {
    const midMarker = this.midMarker;
    if (!midMarker || !midMarker.mesh.visible) return;
    this.markerElapsed += deltaSeconds;
    midMarker.mesh.position.y = MID_MARKER_BASE_Y + Math.sin(this.markerElapsed * 4) * 0.06;
  }

  // --- Sorting interaction (algorithm-agnostic within Sorting, Phase 08) -----
  // Mirrors the Searching interaction block above one-for-one:
  // tryActSort/trySortDecision dispatch through whichever SortController is
  // loaded, applySortOutcome fans the result out to the scene and to
  // GameRuntime's callbacks, exactly like applySearchOutcome does for
  // Searching — replacing the three separate per-algorithm interaction
  // blocks (Bubble Sort, Selection Sort, Insertion Sort) Phases 05/06/07
  // each added.

  private tryActSort(nearestIndex: number | null): void {
    const sortController = this.sortController;
    if (!sortController) return;
    this.applySortOutcome(sortController.handlePrimaryAction(nearestIndex));
  }

  private trySortDecision(decision: SortDecision): void {
    const sortController = this.sortController;
    if (!sortController) return;
    this.applySortOutcome(sortController.handleDecision(decision));
  }

  private applySortOutcome(outcome: SortActionOutcome): void {
    switch (outcome.kind) {
      case "accepted":
        playSound(actionSound(outcome.result.view.isComplete,outcome.result.operations));
        this.player?.react(outcome.result.view.isComplete?'success':'scan');
        this.applySortViewToBoxes(outcome.result.view);
        this.updateSortComparePointer(outcome.result.view);
        this.callbacks.onSortViewStep?.(outcome.result.view, outcome.result.operations);
        break;
      case "rejected-wrong-box":
        this.player?.react('error');playSound('error');
        this.callbacks.onSortActionRejected?.(outcome.expectedIndex);
        break;
      case "rejected-wrong-decision":
        this.player?.react('error');playSound('error');
        this.callbacks.onSortDecisionRejected?.();
        break;
      case "ignored":
        break;
    }
  }

  /**
   * Paints every box from a single SortViewState, regardless of which
   * algorithm produced it. `sortedIndices` and `stickyIndex` are already
   * mutually exclusive by construction in every concrete controller's
   * `toView()` (Bubble Sort's is always null; Selection Sort's and
   * Insertion Sort's `sortedIndices` never include their own sticky
   * index — see InsertionSortController's `computeSortedIndices` doc
   * comment), so precedence between them never actually matters, but
   * `sorted > sticky(candidate) > comparing > default` is kept as the
   * explicit order to match the precedence each of the three retired
   * per-algorithm methods used.
   */
  private applySortViewToBoxes(view: SortViewState): void {
    const sortBoxRow = this.sortBoxRow;
    if (!sortBoxRow) return;
    sortBoxRow.setValues(view.values);
    for (let i = 0; i < view.values.length; i += 1) {
      const isSorted = view.sortedIndices.includes(i);
      const isSticky = view.stickyIndex === i && !isSorted;
      const isComparing = view.awaitingDecision && view.compareIndices.includes(i);
      sortBoxRow.setState(i, isSorted ? "sorted" : isSticky ? "candidate" : isComparing ? "comparing" : "default");
    }
  }

  /**
   * Bubble Sort has no single "required box" — the player must stand
   * between an adjacent pair — so it alone positions the pointer at the
   * pair's midpoint; every other algorithm (Selection Sort's scan box,
   * Insertion Sort's comparison box) positions it at `requiredIndex`
   * directly, mirroring updateSelectionComparePointer/
   * updateInsertionComparePointer's identical single-box logic exactly.
   */
  private updateSortComparePointer(view: SortViewState): void {
    const comparePointer = this.comparePointer;
    const sortBoxRow = this.sortBoxRow;
    if (!comparePointer || !sortBoxRow) return;
    if (view.isComplete) {
      comparePointer.setVisible(false);
      return;
    }
    if (view.algorithm === "bubble-sort") {
      const [left, right] = view.compareIndices;
      const leftX = sortBoxRow.getBoxWorldX(left);
      const rightX = sortBoxRow.getBoxWorldX(right);
      comparePointer.mesh.position.set((leftX + rightX) / 2, COMPARE_POINTER_BASE_Y, 0);
    } else {
      const x = sortBoxRow.getBoxWorldX(view.requiredIndex);
      comparePointer.mesh.position.set(x, COMPARE_POINTER_BASE_Y, 0);
    }
    comparePointer.setVisible(true);
  }

  // --- Sorting shared (bob animation applies to whichever pointer is visible) -

  private updateComparePointerBob(deltaSeconds: number): void {
    const comparePointer = this.comparePointer;
    if (!comparePointer || this.stage !== "sorting" || !comparePointer.mesh.visible) return;
    this.markerElapsed += deltaSeconds;
    comparePointer.mesh.position.y = COMPARE_POINTER_BASE_Y + Math.sin(this.markerElapsed * 4) * 0.06;
  }

  // --- Shared lifecycle -----------------------------------------------------

  private pointerStart = new THREE.Vector2();
  private onPointerDown = (event: PointerEvent): void => { this.pointerStart.set(event.clientX,event.clientY); };
  private onPointerUp = (event: PointerEvent): void => {
    if(this.guidedSearch || this.isPaused || !this.cameraRig || this.pointerStart.distanceTo(new THREE.Vector2(event.clientX,event.clientY)) > 6) return;
    const rect=this.canvas.getBoundingClientRect();
    const ray=new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2((event.clientX-rect.left)/rect.width*2-1,-(event.clientY-rect.top)/rect.height*2+1),this.cameraRig.camera);
    const row=this.boxRow ?? this.sortBoxRow;
    if(!row) return;
    const hits=ray.intersectObject(row.group,true);
    if(hits[0]) this.player?.moveToX(row.getBoxWorldX(row.getNearestIndex(hits[0].point.x)));
  };
  private onMove = (event: Event): void => {
    if(this.guidedSearch || this.isPaused) return;
    const row=this.boxRow ?? this.sortBoxRow; if(!row || !this.player) return;
    const index=row.getNearestIndex(this.player.getPositionX()) + (event as CustomEvent<number>).detail;
    this.player.moveToX(THREE.MathUtils.clamp(row.getBoxWorldX(index),row.bounds.minX,row.bounds.maxX));
  };
  private handleResize = (): void => {
    const renderer = this.renderer;
    const cameraRig = this.cameraRig;
    if (!renderer || !cameraRig) return;
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    renderer.setSize(width, height, false);
    cameraRig.setAspect(width / Math.max(height, 1));
  };

  private tickSearching(player: PlayerRobotHandles, input: GridInputController, deltaSeconds: number): void {
    const boxRow = this.boxRow;
    if (!boxRow) return;
    if(this.manualSearch){
      input.clear();const index=this.manualSearch.index;
      if(index!==null){const x=boxRow.getBoxWorldX(index);player.moveToX(x);player.update(deltaSeconds,0,boxRow.bounds);if(Math.abs(player.getPositionX()-x)<0.05)this.callbacks.onNearestBoxChange?.(index);}
      return;
    }
    if(this.guidedSearch){
      input.clear();
      const view=this.controller?.getView();
      if(this.bugDemo&&view&&view.comparisons>=1){
        const mid=Math.floor((view.values.length-1)/2);
        const wrongRight=view.values[mid]!>view.target;
        const index=wrongRight?Math.floor((mid+1+view.values.length-1)/2):Math.floor((mid-1)/2);
        const x=boxRow.getBoxWorldX(index);player.moveToX(x);player.update(deltaSeconds,0,boxRow.bounds);
        boxRow.setNearestIndex(index);
        view.values.forEach((value,i)=>{if(wrongRight?i<=mid:i>=mid)boxRow.setBoxState(i,'eliminated',value);});
        if(this.midMarker)this.midMarker.mesh.position.x=x;
        if(Math.abs(player.getPositionX()-x)<0.05)this.callbacks.onNearestBoxChange?.(index);
        return;
      }
      if(view?.awaitingChoice&&view.comparisons<=this.guidedComparisons&&view.focusIndex!==null){this.tryChooseDirection(view.values[view.focusIndex]!<view.target?'right':'left');return;}
      const index=view?.focusIndex;
      if(index!==null&&index!==undefined){
        const x=boxRow.getBoxWorldX(index);
        player.moveToX(x);player.update(deltaSeconds,0,boxRow.bounds);
        boxRow.setNearestIndex(index);
        if(view!.comparisons<this.guidedComparisons&&Math.abs(player.getPositionX()-x)<0.05)this.tryAct(index);
      }else player.update(deltaSeconds,0,boxRow.bounds);
      return;
    }
    const moveDirection = this.controller?.getView().awaitingChoice ? 0 : input.getMoveDirection();
    player.update(deltaSeconds, moveDirection, boxRow.bounds);

    const nearestIndex = boxRow.getNearestIndex(player.getPositionX());
    if (nearestIndex !== this.lastNearestBoxIndex) {
      this.lastNearestBoxIndex = nearestIndex;
      boxRow.setNearestIndex(nearestIndex);
      this.callbacks.onNearestBoxChange?.(nearestIndex);
    }

    const actionPressed = input.consumeActionPressed();
    const directionChoice = input.consumeDirectionChoice();
    if (actionPressed) this.tryAct(this.lastNearestBoxIndex);
    if (directionChoice) this.tryChooseDirection(directionChoice);
  }

  private tickSorting(player: PlayerRobotHandles, input: GridInputController, deltaSeconds: number): void {
    const sortBoxRow = this.sortBoxRow;
    if (!sortBoxRow) return;
    if(this.manualSort){input.clear();const i=this.manualSort.pair;if(i!==null)player.moveToX(sortBoxRow.getBoxWorldX(i));player.update(deltaSeconds,0,sortBoxRow.bounds);return;}
    const moveDirection = input.getMoveDirection();
    player.update(deltaSeconds, moveDirection, sortBoxRow.bounds);

    const nearestIndex = sortBoxRow.getNearestIndex(player.getPositionX());
    if (nearestIndex !== this.lastNearestBoxIndex) {
      this.lastNearestBoxIndex = nearestIndex;
      sortBoxRow.setNearestIndex(nearestIndex);
      this.callbacks.onNearestBoxChange?.(nearestIndex);
    }

    const actionPressed = input.consumeActionPressed();
    const sortDecision = input.consumeSortDecision();
    if (actionPressed) this.tryActSort(this.lastNearestBoxIndex);
    if (sortDecision) this.trySortDecision(sortDecision);
  }

  private tick = (timestamp: number): void => {
    this.rafHandle = requestAnimationFrame(this.tick);
    if (!this.isRunning) return;

    const sceneHandles = this.sceneHandles;
    const cameraRig = this.cameraRig;
    const renderer = this.renderer;
    if (this.isPaused || !sceneHandles || !cameraRig || !renderer) {
      this.lastTimestamp = timestamp;
      return;
    }

    const deltaSeconds = this.lastTimestamp
      ? Math.min((timestamp - this.lastTimestamp) / 1000, 0.1)
      : 0;
    this.lastTimestamp = timestamp;

    const player = this.player;
    const input = this.input;
    if (player && input) {
      if (this.stage === "sorting") {
        this.tickSorting(player, input, deltaSeconds);
      } else {
        this.tickSearching(player, input, deltaSeconds);
      }
    }

    this.boxRow?.update(deltaSeconds);
    this.sortBoxRow?.update(deltaSeconds);
    this.updateMidMarkerBob(deltaSeconds);
    this.updateComparePointerBob(deltaSeconds);
    cameraRig.update(deltaSeconds);
    renderer.render(sceneHandles.scene, cameraRig.camera);
  };
}
